import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spin, Typography, message } from 'antd';
import { Link } from 'react-router';
import type { CorePromptSummary, PromptPipeline } from '@viq-admin/types';
import {
  PROMPT_PIPELINES,
  PIPELINE_LABELS,
  listCorePrompts,
  listCompanyGrid,
} from '../../api/promptStore';
import { space } from '@viq-admin/ui';
import { PromptReference } from '../prompt-guide/PromptReference';
import { CoreVersionsDrawer } from './CoreVersionsDrawer';

/**
 * AI Core Prompts — one Card per pipeline, sourced from the `PROMPT_PIPELINES`
 * registry (not the fetched summaries), so every pipeline renders a shell
 * even if its summary is still loading. Each Card shows the active version,
 * note, version count, and (when any company has drifted) a "behind active"
 * rollout-status banner linking to `/prompt-rollout`.
 *
 * "Version history" opens `CoreVersionsDrawer` for that pipeline — it is
 * deliberately ungated here (a read action, same rationale as
 * `PromptTemplateDetail`'s "View companies"). The gated "New version" /
 * "Activate" / "Rollback" controls live INSIDE the drawer, not duplicated on
 * this Card — see `CoreVersionsDrawer`'s header comment for why (avoiding two
 * simultaneously-mounted "New version" controls once the drawer is open).
 *
 * Fetch pattern mirrors `AppliedConferencesModal`: bare useEffect + useState
 * + `message.error`, no react-query — the custom `/v1/superadmin/core-prompts*`
 * endpoints have no corresponding Refine resource.
 */
export function CorePromptsList() {
  const [summaries, setSummaries] = useState<CorePromptSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [behindCounts, setBehindCounts] = useState<Partial<Record<PromptPipeline, number>>>({});
  const [drawerPipeline, setDrawerPipeline] = useState<PromptPipeline | null>(null);

  function refreshSummaries() {
    return listCorePrompts()
      .then(setSummaries)
      .catch((err) =>
        message.error((err as { message?: string }).message ?? 'Failed to load core prompts'),
      );
  }

  /**
   * Re-fetches every pipeline's behind-active count. Re-running for both
   * pipelines (rather than a single one) is deliberate and cheap — there are
   * only ever two — and keeps this usable from both the mount effect (no
   * pipeline context yet) and `refreshAll` below.
   */
  function refreshBehindCounts() {
    return Promise.all(
      PROMPT_PIPELINES.map((pipeline) =>
        listCompanyGrid(pipeline)
          .then((rows) => {
            const behind = rows.filter((r) => r.isBehindActive).length;
            setBehindCounts((prev) => ({ ...prev, [pipeline]: behind }));
          })
          .catch((err) =>
            message.error((err as { message?: string }).message ?? 'Failed to load rollout status'),
          ),
      ),
    );
  }

  /**
   * Passed to the drawer as `onChanged`. A version create/activate can shift
   * BOTH the active-version summary AND which companies now read as
   * "behind" — that second part is exactly what a promotion invalidates —
   * so a mutation must refresh both, not just `listCorePrompts`.
   */
  function refreshAll() {
    return Promise.all([refreshSummaries(), refreshBehindCounts()]);
  }

  useEffect(() => {
    setLoading(true);
    refreshSummaries().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshBehindCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summaryByPipeline = new Map(summaries.map((s) => [s.pipeline, s]));

  return (
    <>
      <Typography.Title level={3}>AI Core Prompts</Typography.Title>
      <Typography.Paragraph type="secondary">
        Activating a version does not change existing companies — they keep their pinned version
        until you roll it out. <Link to="/prompt-guide">How this works</Link>
      </Typography.Paragraph>
      <PromptReference sections={['assembly', 'output', 'writing']} />
      {loading ? (
        <Spin />
      ) : (
        <Row gutter={[16, 16]}>
          {PROMPT_PIPELINES.map((pipeline) => {
            const summary = summaryByPipeline.get(pipeline);
            const behind = behindCounts[pipeline] ?? 0;
            return (
              <Col xs={24} lg={12} key={pipeline}>
                <Card title={PIPELINE_LABELS[pipeline]} data-testid={`core-prompt-card-${pipeline}`}>
                  <Typography.Paragraph>
                    {`Active version ${summary?.activeVersion ?? '—'}`}
                  </Typography.Paragraph>
                  {summary?.note && (
                    <Typography.Paragraph type="secondary">{summary.note}</Typography.Paragraph>
                  )}
                  <Typography.Paragraph type="secondary">
                    {`${summary?.versionCount ?? 0} version(s)`}
                  </Typography.Paragraph>
                  {behind > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: space.md }}
                      message={`${behind} compan${behind === 1 ? 'y' : 'ies'} behind active`}
                      action={<Link to="/prompt-rollout">Review rollout</Link>}
                    />
                  )}
                  <Button onClick={() => setDrawerPipeline(pipeline)}>Version history</Button>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {drawerPipeline && (
        <CoreVersionsDrawer
          pipeline={drawerPipeline}
          open
          onClose={() => setDrawerPipeline(null)}
          onChanged={refreshAll}
        />
      )}
    </>
  );
}
